import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';
import { IconChevronDown, IconPlus, IconTrash, IconRestore } from '@tabler/icons-react';
import type { TaskStatus, TaskDropdownDefinition } from '@techgitter/contracts';
import type { LiveChecklist, ManualTaskInput, ChecklistProgress } from '../../types/taskList';
import { computeSectionProgress } from '../../utils/checklistEngine';
import { TaskRow } from './TaskRow';
import { AddManualTaskDialog } from './AddManualTaskDialog';

interface ChecklistPanelProps {
  checklist: LiveChecklist;
  progress: ChecklistProgress;
  disabled: boolean;
  /** Live dropdown catalog — dropdown-task options are resolved from this at
   *  render time so they appear as soon as the catalog loads. */
  taskDropdowns?: TaskDropdownDefinition[];
  onSetStatus: (sectionId: string, taskId: string, status: TaskStatus) => void;
  onSetNote: (sectionId: string, taskId: string, note: string) => void;
  onSetOption: (sectionId: string, taskId: string, option: string) => void;
  onRemoveTask: (sectionId: string, taskId: string) => void;
  onRestoreTask: (sectionId: string, taskId: string) => void;
  onRemoveSection: (sectionId: string) => void;
  onRestoreSection: (sectionId: string) => void;
  onAddManualTask: (input: ManualTaskInput) => void;
}

function ProgressLabel({ total, closed }: { total: number; closed: number }) {
  const allDone = total > 0 && closed === total;
  return (
    <Typography
      variant="caption"
      sx={{ color: allDone ? 'success.main' : 'text.secondary', fontWeight: allDone ? 600 : 400 }}
    >
      {closed} of {total}
    </Typography>
  );
}

export const ChecklistPanel: React.FC<ChecklistPanelProps> = ({
  checklist,
  progress,
  disabled,
  taskDropdowns,
  onSetStatus,
  onSetNote,
  onSetOption,
  onRemoveTask,
  onRestoreTask,
  onRemoveSection,
  onRestoreSection,
  onAddManualTask,
}) => {
  const [addTaskDialogSectionId, setAddTaskDialogSectionId] = useState<string | null>(null);

  const optionsById = useMemo(
    () => new Map((taskDropdowns ?? []).map(d => [d.dropdownId, d.options])),
    [taskDropdowns],
  );

  const visibleSections = useMemo(
    () => checklist.sections.filter(s => s.isVisible || s.isRemoved),
    [checklist.sections],
  );

  if (visibleSections.length === 0) return null;

  // Unique group labels within a section, rendered as subheadings
  const renderGroupLabel = (label: string) => (
    <Typography
      key={`group-${label}`}
      variant="subtitle2"
      sx={{ px: 2, pt: 1.5, pb: 0.5, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
    >
      {label}
    </Typography>
  );

  return (
    <Box>

      {visibleSections.map(section => {
        if (section.isRemoved) {
          return (
            <Box
              key={section.sectionId}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, opacity: 0.5 }}
            >
              <Typography variant="body2" sx={{ textDecoration: 'line-through', flex: 1 }}>
                {section.label} (removed)
              </Typography>
              <Tooltip title="Restore section">
                <IconButton
                  size="small"
                  onClick={() => onRestoreSection(section.sectionId)}
                  disabled={disabled}
                >
                  <IconRestore size={16} stroke={1.5} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }

        const sectionProgress = computeSectionProgress(section);

        // Build task rows with group subheadings
        let lastGroupLabel: string | undefined;
        const taskElements: React.ReactNode[] = [];

        for (const task of section.tasks) {
          if (!task.isVisible && !task.isRemoved) continue;

          if (task.groupLabel && task.groupLabel !== lastGroupLabel && !task.isRemoved) {
            lastGroupLabel = task.groupLabel;
            taskElements.push(renderGroupLabel(task.groupLabel));
          }

          taskElements.push(
            <TaskRow
              key={task.taskId}
              task={task}
              sectionId={section.sectionId}
              disabled={disabled}
              resolvedOptions={
                task.dropdownId
                  ? optionsById.get(task.dropdownId) ?? task.options ?? []
                  : undefined
              }
              onSetStatus={status => onSetStatus(section.sectionId, task.taskId, status)}
              onSetNote={note => onSetNote(section.sectionId, task.taskId, note)}
              onSetOption={option => onSetOption(section.sectionId, task.taskId, option)}
              onRemove={() => onRemoveTask(section.sectionId, task.taskId)}
              onRestore={() => onRestoreTask(section.sectionId, task.taskId)}
            />,
          );
        }

        return (
          <Accordion key={section.sectionId} defaultExpanded disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<IconChevronDown size={20} stroke={1.5} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                  {section.label}
                </Typography>
                <ProgressLabel total={sectionProgress.total} closed={sectionProgress.closed} />
                <Tooltip title="Remove section">
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveSection(section.sectionId);
                    }}
                    disabled={disabled}
                  >
                    <IconTrash size={16} stroke={1.5} />
                  </IconButton>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pb: 1 }}>
              {taskElements}
              <Box sx={{ px: 2, pt: 1 }}>
                <Button
                  size="small"
                  startIcon={<IconPlus size={16} stroke={1.5} />}
                  onClick={() => setAddTaskDialogSectionId(section.sectionId)}
                  disabled={disabled}
                >
                  Add Task
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <AddManualTaskDialog
        open={addTaskDialogSectionId !== null}
        sectionId={addTaskDialogSectionId ?? ''}
        sections={checklist.sections.filter(s => s.isVisible && !s.isRemoved)}
        onConfirm={input => {
          onAddManualTask(input);
          setAddTaskDialogSectionId(null);
        }}
        onClose={() => setAddTaskDialogSectionId(null)}
      />
    </Box>
  );
};
